import {
  ExternalScenesBuildResult,
  ExternalScenesFilterState,
  ExternalScenesSearchRequest
} from './external-scenes.models';

/** The backend rejects periods longer than this. */
export const MAX_PERIOD_DAYS = 366;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** `yyyy-MM-dd` from local calendar fields (no timezone shift). */
export function formatDateOnly(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * Builds the Search request. Geometry priority: drawn polygon, else region, else map bounds;
 * exactly one geometry field is sent.
 */
export function buildSearchRequest(filters: ExternalScenesFilterState): ExternalScenesBuildResult {
  const { startDate, endDate } = filters;
  if (!startDate || !endDate) {
    return { ok: false, errorKey: 'ERROR_PERIOD_REQUIRED' };
  }
  if (startDate.getTime() > endDate.getTime()) {
    return { ok: false, errorKey: 'ERROR_PERIOD_INVALID' };
  }
  const days = Math.round((endDate.getTime() - startDate.getTime()) / MS_PER_DAY);
  if (days > MAX_PERIOD_DAYS) {
    return { ok: false, errorKey: 'ERROR_PERIOD_TOO_LONG' };
  }

  const base: ExternalScenesSearchRequest = {
    startDate: formatDateOnly(startDate),
    endDate: formatDateOnly(endDate),
    providers: filters.provider === 'both' ? ['Sentinel2', 'Landsat'] : [filters.provider]
  };
  if (filters.maxCloudCover !== null && filters.maxCloudCover >= 0 && filters.maxCloudCover < 100) {
    base.maximumCloudCover = filters.maxCloudCover;
  }

  if (filters.drawnGeoJson) {
    return { ok: true, request: { ...base, geoJson: filters.drawnGeoJson } };
  }
  if (filters.selectedRegionId) {
    return { ok: true, request: { ...base, regionIds: [filters.selectedRegionId] } };
  }
  if (filters.mapBounds) {
    const { west, south, east, north } = filters.mapBounds;
    return { ok: true, request: { ...base, boundingBox: { minLon: west, minLat: south, maxLon: east, maxLat: north } } };
  }
  return { ok: false, errorKey: 'ERROR_GEOMETRY_REQUIRED' };
}
